/**
 * Settings form component.
 * Dynamic form for connector settings based on JSON schema.
 */

// React Imports
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import type { ConnectorSettingsDto } from '../../types/connector';

export interface SettingsFormProps {
  settings?: ConnectorSettingsDto[];
  settingsJson?: string;
  level?: 'System' | 'Tenant' | 'Project' | 'Instance';
  onChange?: (settingsJson: string) => void;
  readOnly?: boolean;
}

/**
 * Settings form component.
 * Renders a dynamic form based on settings JSON schema.
 */
export const SettingsForm = ({
  settings,
  settingsJson,
  level = 'Instance',
  onChange,
  readOnly = false,
}: SettingsFormProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settingsJson) {
      try {
        const parsed = JSON.parse(settingsJson);
        setFormData(parsed);
      } catch {
        setFormData({});
      }
    } else if (settings && settings.length > 0) {
      // Merge settings from hierarchy (Instance overrides Project, etc.)
      const merged: Record<string, any> = {};
      settings.forEach((s) => {
        try {
          const parsed = JSON.parse(s.settingsJson);
          Object.assign(merged, parsed);
        } catch {
          // Ignore invalid JSON
        }
      });
      setFormData(merged);
    }
  }, [settingsJson, settings]);

  // For now, render a generic settings editor
  // In a full implementation, this would parse a JSON schema and render appropriate fields
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            Settings Configuration
          </Typography>
          {level && (
            <Chip
              label={`Level: ${level}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {settings && settings.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Settings are inherited from: {settings.map((s) => s.level).join(' → ')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Settings JSON"
            value={JSON.stringify(formData, null, 2)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setFormData(parsed);
                setErrors({});
                if (onChange) {
                  onChange(e.target.value);
                }
              } catch (error) {
                setErrors({ json: 'Invalid JSON format' });
              }
            }}
            multiline
            rows={10}
            fullWidth
            disabled={readOnly}
            error={!!errors.json}
            helperText={errors.json || 'Enter settings as JSON. This will be merged with inherited settings.'}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />

          {Object.keys(formData).length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current Settings:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}
              >
                <pre style={{ margin: 0 }}>{JSON.stringify(formData, null, 2)}</pre>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
